/**
 * SEO per ogni pagina: un solo posto dove definire title, description, breadcrumb e H1.
 * Ogni pagina usa questi dati per SeoHead e per il contenuto.
 */
import { SEO_ROUTES } from "./seo-routes";

export type SeoPageId = keyof typeof SEO_ROUTES;

export type PageSeo = {
  /** Meta title (~60 caratteri) */
  title: string;
  /** Meta description (~155 caratteri) */
  description: string;
  /** Nome per breadcrumb e link (es. "QR Code WhatsApp") */
  breadcrumbName: string;
  /** H1 della pagina (un solo H1 per pagina) */
  h1: string;
};

/** SEO per ogni route: titolo, descrizione, breadcrumb, H1 */
export const PAGE_SEO: Record<SeoPageId, PageSeo> = {
  home: {
    title: "Creare QR Code Gratis Online | Personalizzato Senza Registrazione",
    description:
      "Crea QR Code gratis online senza registrazione. Personalizzali con logo, colori e scaricali subito. Generatore per URL, WiFi, WhatsApp, PDF e menu.",
    breadcrumbName: "Home",
    h1: "Crea un QR Code Personalizzato Gratis",
  },
  generator: {
    title: "Creare QR Code Gratis Online | Personalizzato Senza Registrazione",
    description:
      "Crea QR Code gratis online senza registrazione. Personalizzali con logo, colori e scaricali subito. Generatore per URL, WiFi, WhatsApp, PDF e menu.",
    breadcrumbName: "Generatore QR Code",
    h1: "Crea un QR Code Personalizzato Gratis",
  },
  whatsapp: {
    title: "QR Code WhatsApp Gratis | Crea Link e Chat con un Tap",
    description:
      "Crea un QR Code per WhatsApp gratis: link al profilo, chat o gruppo. Personalizzabile, senza registrazione. Guida pratica e generatore incluso.",
    breadcrumbName: "QR Code WhatsApp",
    h1: "QR Code WhatsApp: guida e generatore gratis",
  },
  wifi: {
    title: "QR Code WiFi Gratis | Condividi la Rete con un Tap",
    description:
      "Crea un QR Code WiFi gratis: inserisci SSID e password, scarica e stampa. I clienti si connettono inquadrando il codice. Guida e generatore senza registrazione.",
    breadcrumbName: "QR Code WiFi",
    h1: "QR Code WiFi: condividi la rete senza digitare la password",
  },
  instagram: {
    title: "QR Code Instagram Gratis | Profilo, Post e Link",
    description:
      "Crea un QR Code per Instagram gratis: profilo, post o link. Personalizzabile, senza registrazione. Guida pratica e generatore incluso.",
    breadcrumbName: "QR Code Instagram",
    h1: "QR Code Instagram: porta i follower al tuo profilo con un tap",
  },
  pdf: {
    title: "QR Code PDF Gratis | Link per Aprire o Scaricare Documenti",
    description:
      "Crea un QR Code che apre o scarica un PDF gratis. Incolla il link del file, personalizza e stampa. Guida e generatore senza registrazione.",
    breadcrumbName: "QR Code PDF",
    h1: "QR Code PDF: apri o scarica documenti con un tap",
  },
  menuRistorante: {
    title: "QR Code Menu Ristorante Gratis | Menu Digitale con un Tap",
    description:
      "Crea un QR Code per il menu del ristorante gratis. I clienti inquadrano e aprono il menu sul telefono. Guida pratica e generatore senza registrazione.",
    breadcrumbName: "QR Code Menu Ristorante",
    h1: "QR Code Menu Ristorante: guida e generatore gratis",
  },
  cosEUnQrCode: {
    title: "Cos'è un QR Code e Come Funziona | Guida Completa",
    description:
      "Scopri cos'è un QR code, come funziona e come creare qr code gratis. Guida pratica con esempi per URL, WiFi, WhatsApp e stampa.",
    breadcrumbName: "Cos'è un QR Code",
    h1: "Cos'è un QR Code e come funziona",
  },
  qrCodeDinamicoVsStatico: {
    title: "QR Code Dinamico vs Statico: Differenze e Quando Usarli",
    description:
      "QR code dinamico e statico a confronto: differenze, vantaggi e quando creare qr code gratis (statico) o usare servizi dinamici.",
    breadcrumbName: "QR Code Dinamico vs Statico",
    h1: "QR Code dinamico vs statico: guida pratica",
  },
  qrCodeConLogo: {
    title: "Come Creare un QR Code con Logo | Guida Passo Passo",
    description:
      "Guida pratica per creare un QR code con logo al centro: correzione errori, dimensioni, contrasto. Generatore gratis senza registrazione.",
    breadcrumbName: "QR Code con Logo",
    h1: "Come creare un QR Code con logo",
  },
  qrCodeBigliettiDaVisita: {
    title: "QR Code per Biglietti da Visita | Guida e Generatore Gratis",
    description:
      "Aggiungi un QR code al biglietto da visita: link al profilo, sito, WhatsApp o vCard. Guida pratica e generatore senza registrazione.",
    breadcrumbName: "QR Code Biglietti da Visita",
    h1: "QR Code per biglietti da visita: guida e generatore",
  },
  qrCodeEventi: {
    title: "QR Code per Eventi | Registrazione, WiFi e Link",
    description:
      "Usa QR code per eventi: registrazione, WiFi, programma, biglietti. Guida pratica e generatore gratis per organizzatori.",
    breadcrumbName: "QR Code per Eventi",
    h1: "QR Code per eventi: guida pratica",
  },
};

/** Restituisce i breadcrumb per una pagina (Home + pagina corrente) */
export function getBreadcrumbsForPage(pageId: SeoPageId): Array<{ name: string; url: string }> {
  if (pageId === "home") return [{ name: PAGE_SEO.home.breadcrumbName, url: SEO_ROUTES.home }];
  return [
    { name: PAGE_SEO.home.breadcrumbName, url: SEO_ROUTES.home },
    { name: PAGE_SEO[pageId].breadcrumbName, url: SEO_ROUTES[pageId] },
  ];
}
